// ManifestoDestinatarioPage.js
// Página de Manifesto do Destinatário - Gestão de NF-e Recebidas

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';
import styles from '../Orders/Orders.module.css';
import {
    PageHeader,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    FormModal,
    Toast,
    StatusBadge,
    ConfirmModal
} from '../../components/common';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const NFE_MANIFEST_COLLECTION = 'nfe-manifest';
const ITEMS_PER_PAGE = 15;

// Estados do Manifesto
const MANIFEST_STATUS = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    DENIED: 'denied',
    UNKNOWN: 'unknown'
};

const MANIFEST_STATUS_LABELS = {
    [MANIFEST_STATUS.PENDING]: { label: 'Pendente', className: 'statusBadgePending' },
    [MANIFEST_STATUS.CONFIRMED]: { label: 'Confirmada', className: 'statusBadgeSuccess' },
    [MANIFEST_STATUS.DENIED]: { label: 'Negada', className: 'statusBadgeDanger' },
    [MANIFEST_STATUS.UNKNOWN]: { label: 'Desconhecida', className: 'statusBadgeWarning' }
};

// Códigos de Evento do Manifesto
const MANIFEST_EVENTS = {
    CONFIRMATION: '210200',
    UNKNOWN_OPERATION: '210220',
    NOT_PERFORMED: '210240'
};

const MANIFEST_EVENTS_LABELS = {
    [MANIFEST_EVENTS.CONFIRMATION]: {
        label: 'Confirmar Operação',
        description: 'Confirmo que recebi a mercadoria e a operação ocorreu normalmente.',
        requiresJustification: false,
        actionColor: 'success'
    },
    [MANIFEST_EVENTS.UNKNOWN_OPERATION]: {
        label: 'Desconhecer Operação',
        description: 'Declaro que esta operação não foi realizada por mim e não reconheço esta NF-e.',
        requiresJustification: false,
        actionColor: 'warning'
    },
    [MANIFEST_EVENTS.NOT_PERFORMED]: {
        label: 'Negar Operação',
        description: 'A operação não foi realizada e a mercadoria não foi recebida.',
        requiresJustification: true,
        minJustificationLength: 15,
        actionColor: 'danger'
    }
};

// ============================================
// 1.1 INTEGRAÇÃO FOCUS NFE (via proxy em Cloud Functions)
// ============================================
// O navegador NUNCA fala diretamente com a Focus NFe — a API não libera CORS
// para chamadas de browser, então a chamada é sempre bloqueada antes de sair.
// Por isso todo o tráfego passa pela Cloud Function `focusNfeProxy`
// (functions/focusNfeProxy.js), que guarda o token em Secret Manager e
// repassa a chamada no servidor, onde CORS não se aplica.
//
// Troque pela URL real da sua função após o deploy, por ex:
// 'https://southamerica-east1-SEU_PROJETO.cloudfunctions.net/focusNfeProxy'
const FOCUS_NFE_PROXY_BASE_URL = 'https://us-central1-b2e-erp.cloudfunctions.net/focusNfeProxy';

// Wrapper genérico para chamadas ao proxy (JSON in/out)
async function focusNfeRequest(path, options = {}) {
    const response = await fetch(`${FOCUS_NFE_PROXY_BASE_URL}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {})
        }
    });

    // Handle 204 No Content - return empty array
    if (response.status === 204) {
        return { ok: true, status: 204, json: async () => [] };
    }

    if (!response.ok) {
        let message = `Erro Focus NFe (HTTP ${response.status})`;
        try {
            const errorBody = await response.json();
            message = errorBody?.mensagem || message;
        } catch (_) {
            // corpo não era JSON, mantém mensagem genérica
        }
        throw new Error(message);
    }

    return response;
}

// Extrai série e número da NF-e a partir da chave de acesso de 44 dígitos
// Layout: cUF(2) AAMM(4) CNPJ(14) mod(2) serie(3) nNF(9) tpEmi(1) cNF(8) cDV(1)
function parseChaveNFe(chave) {
    if (!chave || chave.length !== 44) return { serie: null, numero: null };
    const serie = String(parseInt(chave.substring(22, 25), 10));
    const numero = String(parseInt(chave.substring(25, 34), 10));
    return { serie, numero };
}

// Mapeia o campo "manifestacao_destinatario" da Focus NFe para o status interno
function mapManifestacaoToStatus(manifestacao) {
    switch (manifestacao) {
        case 'confirmacao':
            return MANIFEST_STATUS.CONFIRMED;
        case 'desconhecimento':
            return MANIFEST_STATUS.UNKNOWN;
        case 'nao_realizada':
            return MANIFEST_STATUS.DENIED;
        case 'ciencia':
        case 'nulo':
        default:
            return MANIFEST_STATUS.PENDING;
    }
}

// Mapeia os códigos de evento locais (SEFAZ) para o campo "tipo" esperado pela Focus NFe
const EVENT_CODE_TO_FOCUS_TIPO = {
    [MANIFEST_EVENTS.CONFIRMATION]: 'confirmacao',
    [MANIFEST_EVENTS.UNKNOWN_OPERATION]: 'desconhecimento',
    [MANIFEST_EVENTS.NOT_PERFORMED]: 'nao_realizada'
};

// ============================================
// 2. MODAL DE DETALHES DA NF-e
// ============================================
const NFeDetailModal = ({ nfe, onClose, onPrint, onManifest, isPrinting, t }) => {
    if (!nfe) return null;

    const getStatusClass = (status) => {
        const config = MANIFEST_STATUS_LABELS[status] || MANIFEST_STATUS_LABELS[MANIFEST_STATUS.PENDING];
        return config.className;
    };

    const getStatusLabel = (status) => {
        const config = MANIFEST_STATUS_LABELS[status] || MANIFEST_STATUS_LABELS[MANIFEST_STATUS.PENDING];
        return config.label;
    };

    const productsList = nfe.products || nfe.itens || [];

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerXLarge}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2>{t('pages.recipientManifest.details.title')}</h2>
                        <p className={styles.modalSubtitle}>
                            {t('pages.recipientManifest.details.subtitle', { number: nfe.numero, series: nfe.serie })}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton}>×</button>
                </div>

                <div className={styles.formContent} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>📍</div>
                            <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.details.manifestStatus')}</h3>
                        </div>
                        <div className={styles.infoBox} style={{ backgroundColor: '#e3f2fd' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span>
                                    <strong>{t('common.status')}:</strong>{' '}
                                    <span className={`${styles.statusBadge} ${styles[getStatusClass(nfe.manifestStatus)]}`}>
                                        {getStatusLabel(nfe.manifestStatus)}
                                    </span>
                                </span>
                                {nfe.manifestedAt && (
                                    <span><strong>{t('pages.recipientManifest.details.manifestedAt')}</strong> {nfe.manifestedAt}</span>
                                )}
                                {nfe.manifestedBy && (
                                    <span><strong>{t('pages.recipientManifest.details.manifestedBy')}</strong> {nfe.manifestedBy}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>📋</div>
                            <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.details.nfeData')}</h3>
                        </div>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.number')}:</label>
                                <span>{nfe.numero}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.series')}:</label>
                                <span>{nfe.serie}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.accessKey')}</label>
                                <span style={{ fontSize: '12px', fontFamily: 'monospace' }}>{nfe.chaveAcesso}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.emissionDate')}</label>
                                <span>{nfe.dataEmissao}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.entryDate')}</label>
                                <span>{nfe.dataEntrada || nfe.createdAt || t('common.notInformed')}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.totalValue')}:</label>
                                <span style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                    R$ {nfe.valorTotal?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>🏢</div>
                            <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.details.issuer')}</h3>
                        </div>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.legalName')}</label>
                                <span>{nfe.emitente?.nome || nfe.fornecedorNome || t('common.notInformed')}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.document')}</label>
                                <span>{nfe.emitente?.cnpj || nfe.fornecedorCnpj || t('common.notInformed')}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.ie')}</label>
                                <span>{nfe.emitente?.ie || t('common.notInformed')}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.address')}</label>
                                <span>{nfe.emitente?.endereco || t('common.notInformed')}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>📍</div>
                            <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.details.recipient')}</h3>
                        </div>
                        <div className={styles.infoGrid}>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.legalName')}</label>
                                <span>{nfe.destinatario?.nome || 'Sua Empresa'}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <label>{t('pages.recipientManifest.details.document')}</label>
                                <span>{nfe.destinatario?.cnpj || 'Seu CNPJ'}</span>
                            </div>
                        </div>
                    </div>

                    {productsList.length > 0 && (
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>📦</div>
                                <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.details.products')}</h3>
                                <span className={styles.sectionBadge}>
                                    {t('pages.recipientManifest.details.itemsCount', { count: productsList.length })}
                                </span>
                            </div>
                            <div className={styles.tableContainer}>
                                <table className={styles.itemTable}>
                                    <thead>
                                        <tr>
                                            <th>{t('pages.recipientManifest.details.code')}</th>
                                            <th>{t('pages.recipientManifest.details.description')}</th>
                                            <th>{t('pages.recipientManifest.details.ncm')}</th>
                                            <th>{t('pages.recipientManifest.details.quantity')}</th>
                                            <th>{t('pages.recipientManifest.details.unit')}</th>
                                            <th>{t('pages.recipientManifest.details.unitValue')}</th>
                                            <th>{t('pages.recipientManifest.details.totalValue')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productsList.map((product, index) => (
                                            <tr key={index}>
                                                <td>{product.codigo || product.code || '-'}</td>
                                                <td>{product.descricao || product.name || '-'}</td>
                                                <td>{product.ncm || '-'}</td>
                                                <td>{product.quantidade || product.quantity || '-'}</td>
                                                <td>{product.unidade || product.unit || '-'}</td>
                                                <td>R$ {(product.valorUnitario || product.unitPrice || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                                <td>R$ {(product.valorTotal || product.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>💰</div>
                            <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.details.fiscalValues')}</h3>
                        </div>
                        <div className={styles.infoGrid}>
                            {nfe.baseCalculoIcms > 0 && (
                                <div className={styles.infoItem}>
                                    <label>{t('pages.recipientManifest.details.icmsBase')}</label>
                                    <span>R$ {nfe.baseCalculoIcms.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                </div>
                            )}
                            {nfe.valorIcms > 0 && (
                                <div className={styles.infoItem}>
                                    <label>{t('pages.recipientManifest.details.icmsValue')}</label>
                                    <span>R$ {nfe.valorIcms.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                </div>
                            )}
                            {nfe.valorIpi > 0 && (
                                <div className={styles.infoItem}>
                                    <label>{t('pages.recipientManifest.details.ipiValue')}</label>
                                    <span>R$ {nfe.valorIpi.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                </div>
                            )}
                            {nfe.valorPis > 0 && (
                                <div className={styles.infoItem}>
                                    <label>{t('pages.recipientManifest.details.pisValue')}</label>
                                    <span>R$ {nfe.valorPis.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                </div>
                            )}
                            {nfe.valorCofins > 0 && (
                                <div className={styles.infoItem}>
                                    <label>{t('pages.recipientManifest.details.cofinsValue')}</label>
                                    <span>R$ {nfe.valorCofins.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {nfe.protocoloAutorizacao && (
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}>🔐</div>
                                <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.details.protocols')}</h3>
                            </div>
                            <div className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <label>{t('pages.recipientManifest.details.authorizationProtocol')}</label>
                                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{nfe.protocoloAutorizacao}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.secondaryButton}>
                        {t('pages.recipientManifest.details.close')}
                    </button>
                    <button type="button" onClick={onPrint} className={styles.primaryButton} disabled={isPrinting}>
                        {isPrinting ? '🖨️...' : `🖨️ ${t('pages.recipientManifest.details.printDanfe')}`}
                    </button>
                    {nfe.manifestStatus === MANIFEST_STATUS.PENDING && (
                        <button type="button" onClick={() => onManifest(nfe)} className={styles.approveActionButton}>
                            {t('pages.recipientManifest.details.manifest')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// 3. MODAL DE MANIFESTAÇÃO
// ============================================
const ManifestModal = ({ nfe, onClose, onConfirm, isLoading, t }) => {
    const [selectedEvent, setSelectedEvent] = useState(MANIFEST_EVENTS.CONFIRMATION);
    const [justification, setJustification] = useState('');
    const [errors, setErrors] = useState({});

    if (!nfe) return null;

    const eventConfig = MANIFEST_EVENTS_LABELS[selectedEvent];
    const requiresJustification = eventConfig?.requiresJustification || false;
    const minLength = eventConfig?.minJustificationLength || 15;

    const validateForm = () => {
        const newErrors = {};
        if (requiresJustification && (!justification || justification.trim().length < minLength)) {
            newErrors.justification = t('pages.recipientManifest.manifest.justificationRequired', { minLength });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirm = (e) => {
        // Mesma defesa: bloqueia o submit nativo do <form>, que recarregaria a
        // página e cancelaria a chamada de manifestação em andamento.
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (!validateForm()) return;
        onConfirm({
            nfeId: nfe.id,
            chaveAcesso: nfe.chaveAcesso,
            numero: nfe.numero,
            eventCode: selectedEvent,
            justification: requiresJustification ? justification : null,
            manifestStatus: selectedEvent === MANIFEST_EVENTS.CONFIRMATION ? MANIFEST_STATUS.CONFIRMED :
                selectedEvent === MANIFEST_EVENTS.NOT_PERFORMED ? MANIFEST_STATUS.DENIED : MANIFEST_STATUS.UNKNOWN
        });
    };

    const getEventColor = (eventCode) => {
        const config = MANIFEST_EVENTS_LABELS[eventCode];
        switch (config?.actionColor) {
            case 'success': return 'var(--success-color)';
            case 'danger': return 'var(--danger-color)';
            case 'warning': return 'var(--warning-color)';
            default: return 'var(--primary-color)';
        }
    };

    return (
        <FormModal
            isOpen={true}
            onClose={onClose}
            onSubmit={handleConfirm}
            title={t('pages.recipientManifest.manifest.title')}
            subtitle={t('pages.recipientManifest.manifest.subtitle', { number: nfe.numero, issuer: nfe.emitente?.nome || nfe.fornecedorNome })}
            isLoading={isLoading}
            submitText={isLoading ? t('pages.recipientManifest.manifest.sending') :
                t('pages.recipientManifest.manifest.confirmButton', {
                    label: selectedEvent === MANIFEST_EVENTS.CONFIRMATION ? t('pages.recipientManifest.manifest.confirmOperation') :
                        selectedEvent === MANIFEST_EVENTS.UNKNOWN_OPERATION ? t('pages.recipientManifest.manifest.unknownOperation') :
                            t('pages.recipientManifest.manifest.denyOperation')
                })}
            maxWidth="600px"
        >
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>⚡</div>
                    <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.manifest.manifestType')}</h3>
                    <span className={styles.sectionBadge}>{t('pages.recipientManifest.manifest.selectOption')}</span>
                </div>
                <div style={{ padding: '20px' }}>
                    <div className={styles.formGrid} style={{ gridTemplateColumns: '1fr', gap: '12px' }}>
                        {Object.entries(MANIFEST_EVENTS_LABELS).map(([eventCode, config]) => (
                            <label
                                key={eventCode}
                                className={styles.manifestOption}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px',
                                    padding: '16px',
                                    border: `2px solid ${selectedEvent === eventCode ? getEventColor(eventCode) : 'var(--border-color)'}`,
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    backgroundColor: selectedEvent === eventCode ? `${getEventColor(eventCode)}10` : 'transparent',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <input
                                    type="radio"
                                    name="manifestEvent"
                                    value={eventCode}
                                    checked={selectedEvent === eventCode}
                                    onChange={() => {
                                        setSelectedEvent(eventCode);
                                        setJustification('');
                                        setErrors({});
                                    }}
                                    style={{ marginTop: '2px' }}
                                    disabled={isLoading}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                                        {eventCode === MANIFEST_EVENTS.CONFIRMATION ? t('pages.recipientManifest.manifest.confirmOperation') :
                                            eventCode === MANIFEST_EVENTS.UNKNOWN_OPERATION ? t('pages.recipientManifest.manifest.unknownOperation') :
                                                t('pages.recipientManifest.manifest.denyOperation')}
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                        {eventCode === MANIFEST_EVENTS.CONFIRMATION ? t('pages.recipientManifest.manifest.confirmDescription') :
                                            eventCode === MANIFEST_EVENTS.UNKNOWN_OPERATION ? t('pages.recipientManifest.manifest.unknownDescription') :
                                                t('pages.recipientManifest.manifest.denyDescription')}
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {requiresJustification && (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>✏️</div>
                        <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.manifest.justification')}</h3>
                        <span className={styles.sectionBadge}>{t('common.required')}</span>
                    </div>
                    <div style={{ padding: '20px' }}>
                        <div className={styles.inputGroup}>
                            <textarea
                                value={justification}
                                onChange={(e) => {
                                    setJustification(e.target.value);
                                    if (errors.justification) setErrors({});
                                }}
                                className={styles.textarea}
                                rows={4}
                                placeholder={t('pages.recipientManifest.manifest.justificationPlaceholder', { minLength })}
                                disabled={isLoading}
                            />
                            {errors.justification && (
                                <small className={styles.errorHint}>{errors.justification}</small>
                            )}
                            <small className={styles.fieldHint}>
                                {t('pages.recipientManifest.manifest.characters', { count: justification.length, minLength })}
                            </small>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>📄</div>
                    <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.manifest.nfeSummary')}</h3>
                </div>
                <div className={styles.infoBox} style={{ backgroundColor: '#f8f9fa' }}>
                    <div className={styles.infoGrid}>
                        <div className={styles.infoItem}>
                            <label>{t('pages.recipientManifest.details.number')}:</label>
                            <span>{nfe.numero}</span>
                        </div>
                        <div className={styles.infoItem}>
                            <label>{t('pages.recipientManifest.details.issuer')}:</label>
                            <span>{nfe.emitente?.nome || nfe.fornecedorNome}</span>
                        </div>
                        <div className={styles.infoItem}>
                            <label>{t('pages.recipientManifest.details.totalValue')}:</label>
                            <span style={{ fontWeight: 'bold' }}>
                                R$ {(nfe.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </FormModal>
    );
};

// ============================================
// 4. SYNC MODAL - BUSCAR NF-e NA SEFAZ
// ============================================
const SyncNFeModal = ({ onClose, onSync, isLoading, cnpj, t }) => {
    const [period, setPeriod] = useState('last30');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        setEndDate(today.toISOString().split('T')[0]);
        setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }, []);

    const handleSync = (e) => {
        // Defesa contra submit nativo do <form>: se o FormModal repassar o evento,
        // bloqueamos o comportamento padrão (que recarrega a página inteira e
        // descarta todo o estado React, cancelando a sincronização em andamento).
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        let start = startDate;
        let end = endDate;
        if (period === 'last7') {
            const today = new Date();
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(today.getDate() - 7);
            start = sevenDaysAgo.toISOString().split('T')[0];
            end = today.toISOString().split('T')[0];
        } else if (period === 'last30') {
            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            start = thirtyDaysAgo.toISOString().split('T')[0];
            end = today.toISOString().split('T')[0];
        }
        onSync({ startDate: start, endDate: end });
    };

    return (
        <FormModal
            isOpen={true}
            onClose={onClose}
            onSubmit={handleSync}
            title={t('pages.recipientManifest.syncNFeTitle')}
            subtitle={t('pages.recipientManifest.syncNFeSubtitle')}
            isLoading={isLoading}
            submitText={isLoading ? t('pages.recipientManifest.searchingNFe') : `🔄 ${t('pages.recipientManifest.syncNFe')}`}
            maxWidth="500px"
        >
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>📅</div>
                    <h3 className={styles.sectionTitle}>{t('pages.recipientManifest.period')}</h3>
                </div>
                <div style={{ padding: '20px' }}>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label>{t('pages.recipientManifest.periodLabel')}</label>
                            <select value={period} onChange={(e) => setPeriod(e.target.value)} className={styles.select} disabled={isLoading}>
                                <option value="last7">{t('pages.recipientManifest.last7Days')}</option>
                                <option value="last30">{t('pages.recipientManifest.last30Days')}</option>
                                <option value="custom">{t('pages.recipientManifest.custom')}</option>
                            </select>
                        </div>
                        {period === 'custom' && (
                            <>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.recipientManifest.startDate')}</label>
                                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={styles.input} disabled={isLoading} />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.recipientManifest.endDate')}</label>
                                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={styles.input} disabled={isLoading} />
                                </div>
                            </>
                        )}
                    </div>
                    <div className={styles.infoBox} style={{ marginTop: '16px', backgroundColor: '#e3f2fd' }}>
                        <span>ℹ️</span>
                        <span>{t('pages.recipientManifest.syncInfo', { cnpj: cnpj || t('common.notInformed') })}</span>
                    </div>
                </div>
            </div>
        </FormModal>
    );
};

// ============================================
// 5. COMPONENTE PRINCIPAL
// ============================================
const ManifestoDestinatarioPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('manifesto_do_destinatario');
    const [nfes, setNfes] = useState([]);
    const [filteredNfes, setFilteredNfes] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [toasts, setToasts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [selectedNFe, setSelectedNFe] = useState(null);
    const [manifestingNFe, setManifestingNFe] = useState(null);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [syncInProgress, setSyncInProgress] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const { currentUser } = useAuth();
    const { addDocument, updateDocument, refreshData, documents } = useFirestore(NFE_MANIFEST_COLLECTION);
    const companyCnpj = currentUser?.companyCnpj || '';

    const addToast = useCallback((message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(toast => toast.id !== id)), 5000);
    }, []);

    useEffect(() => {
        let isMounted = true;
        const loadNfes = async () => {
            setIsInitialLoad(true);
            setLoadError(null);
            try {
                const result = await refreshData();
                if (!isMounted) return;
                if (result?.data && Array.isArray(result.data)) {
                    const formattedData = result.data.map(nfe => ({
                        ...nfe,
                        dataEmissao: nfe.dataEmissao ? (typeof nfe.dataEmissao === 'object' && nfe.dataEmissao?.toDate ? nfe.dataEmissao.toDate().toLocaleDateString('pt-BR') : nfe.dataEmissao) : 'Data não informada',
                        manifestedAt: nfe.manifestedAt?.toDate?.()?.toLocaleDateString('pt-BR') || nfe.manifestedAt || null
                    }));
                    setNfes(formattedData);
                } else if (documents && Array.isArray(documents)) {
                    const formattedData = documents.map(nfe => ({
                        ...nfe,
                        dataEmissao: nfe.dataEmissao ? (typeof nfe.dataEmissao === 'object' && nfe.dataEmissao?.toDate ? nfe.dataEmissao.toDate().toLocaleDateString('pt-BR') : nfe.dataEmissao) : 'Data não informada',
                        manifestedAt: nfe.manifestedAt?.toDate?.()?.toLocaleDateString('pt-BR') || nfe.manifestedAt || null
                    }));
                    setNfes(formattedData);
                } else {
                    setNfes([]);
                }
            } catch (error) {
                console.error('Error loading NF-e from Firestore:', error);
                if (isMounted) {
                    setLoadError(error.message);
                    addToast(t('pages.recipientManifest.toasts.loadError'), 'error');
                    setNfes([]);
                }
            } finally {
                if (isMounted) setIsInitialLoad(false);
            }
        };
        loadNfes();
        return () => { isMounted = false; };
    }, [refreshData, documents, addToast, t]);

    useEffect(() => {
        let filtered = [...nfes];
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(nfe =>
                nfe.numero?.toLowerCase().includes(term) ||
                nfe.chaveAcesso?.toLowerCase().includes(term) ||
                nfe.emitente?.nome?.toLowerCase().includes(term) ||
                nfe.fornecedorNome?.toLowerCase().includes(term)
            );
        }
        if (statusFilter) filtered = filtered.filter(nfe => nfe.manifestStatus === statusFilter);
        setFilteredNfes(filtered);
        setCurrentPage(1);
    }, [nfes, searchTerm, statusFilter]);

    const totalPages = Math.ceil(filteredNfes.length / ITEMS_PER_PAGE);
    const currentNfes = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredNfes.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredNfes, currentPage]);

    const getStatusClass = (status) => {
        const config = MANIFEST_STATUS_LABELS[status] || MANIFEST_STATUS_LABELS[MANIFEST_STATUS.PENDING];
        return config.className;
    };

    const getStatusLabel = (status) => {
        const statusMap = {
            [MANIFEST_STATUS.PENDING]: t('pages.recipientManifest.status.pending'),
            [MANIFEST_STATUS.CONFIRMED]: t('pages.recipientManifest.status.confirmed'),
            [MANIFEST_STATUS.DENIED]: t('pages.recipientManifest.status.denied'),
            [MANIFEST_STATUS.UNKNOWN]: t('pages.recipientManifest.status.unknown')
        };
        return statusMap[status] || status;
    };

    const handlePrint = async (nfe) => {
        if (!nfe.chaveAcesso) {
            addToast(t('pages.recipientManifest.toasts.printError'), 'error');
            return;
        }
        setIsPrinting(true);
        try {
            // fetch() segue o redirect 302 da Focus NFe automaticamente e devolve
            // o PDF já baixado da URL assinada (que não exige Authorization).
            const response = await focusNfeRequest(`/nfes-recebidas/${nfe.chaveAcesso}/pdf`, {
                method: 'GET'
            });
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            window.open(blobUrl, '_blank', 'noopener,noreferrer');
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        } catch (error) {
            console.error('Print error:', error);
            addToast(error.message || t('pages.recipientManifest.toasts.printError'), 'error');
        } finally {
            setIsPrinting(false);
        }
    };

    const handleManifestConfirm = async (manifestData) => {
        setIsLoading(true);
        try {
            if (!manifestData.chaveAcesso) {
                throw new Error('Chave de acesso da NF-e não encontrada.');
            }

            const tipo = EVENT_CODE_TO_FOCUS_TIPO[manifestData.eventCode];
            if (!tipo) {
                throw new Error('Tipo de manifestação inválido.');
            }

            const requestBody = { tipo };
            if (tipo === 'nao_realizada') {
                requestBody.justificativa = manifestData.justification;
            }

            // Chama a SEFAZ (via Focus NFe) ANTES de gravar localmente — o Firestore
            // deve refletir o que realmente foi registrado no fisco, não o inverso.
            const response = await focusNfeRequest(
                `/nfes-recebidas/${manifestData.chaveAcesso}/manifesto`,
                { method: 'POST', body: JSON.stringify(requestBody) }
            );
            const result = await response.json();

            const manifestedBy = currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário';
            const updatedNfes = nfes.map(nfe => {
                if (nfe.id === manifestData.nfeId) {
                    return {
                        ...nfe,
                        manifestStatus: manifestData.manifestStatus,
                        manifestedAt: new Date().toLocaleDateString('pt-BR'),
                        manifestedBy,
                        manifestEventCode: manifestData.eventCode,
                        manifestJustification: manifestData.justification,
                        manifestProtocolo: result.protocolo || null
                    };
                }
                return nfe;
            });
            setNfes(updatedNfes);

            await updateDocument(manifestData.nfeId, {
                manifestStatus: manifestData.manifestStatus,
                manifestedAt: new Date(),
                manifestedBy,
                manifestEventCode: manifestData.eventCode,
                manifestJustification: manifestData.justification,
                manifestProtocolo: result.protocolo || null,
                manifestStatusSefaz: result.status_sefaz || null,
                manifestMensagemSefaz: result.mensagem_sefaz || null,
                updatedAt: new Date()
            });

            const eventLabel = manifestData.eventCode === MANIFEST_EVENTS.CONFIRMATION ? t('pages.recipientManifest.manifest.confirmOperation') :
                manifestData.eventCode === MANIFEST_EVENTS.UNKNOWN_OPERATION ? t('pages.recipientManifest.manifest.unknownOperation') :
                    t('pages.recipientManifest.manifest.denyOperation');
            addToast(t('pages.recipientManifest.toasts.manifestSuccess', { event: eventLabel, number: manifestData.numero }), 'success');
            setManifestingNFe(null);
        } catch (error) {
            console.error('Manifest error:', error);
            addToast(error.message || t('pages.recipientManifest.toasts.manifestError'), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSync = async (syncData) => {
        setSyncInProgress(true);
        try {
            const cnpjDigits = (companyCnpj || '').replace(/\D/g, '');
            if (!cnpjDigits) {
                addToast(t('pages.recipientManifest.toasts.syncError'), 'error');
                return;
            }

            let versao = 0;
            let allResults = [];
            let safety = 0;

            while (safety < 50) {
                safety += 1;
                const params = new URLSearchParams({ cnpj: cnpjDigits });
                if (versao > 0) params.set('versao', String(versao));

                const response = await focusNfeRequest(`/nfes-recebidas?${params.toString()}`, {
                    method: 'GET'
                });

                // Check if it's a 204 response
                let page = [];
                if (response.status === 204) {
                    page = [];
                } else {
                    page = await response.json();
                }

                allResults = allResults.concat(page);

                const maxVersionHeader = response.headers.get('X-Max-Version');
                const totalCountHeader = response.headers.get('X-Total-Count');
                const maxVersion = maxVersionHeader ? parseInt(maxVersionHeader, 10) : null;

                const reachedEnd =
                    page.length === 0 ||
                    !maxVersion ||
                    maxVersion <= versao ||
                    (totalCountHeader && allResults.length >= parseInt(totalCountHeader, 10));

                if (reachedEnd) break;
                versao = maxVersion;
            }

            // A API não filtra por data de emissão, então o período do modal é aplicado aqui.
            const startBoundary = syncData.startDate ? new Date(`${syncData.startDate}T00:00:00`) : null;
            const endBoundary = syncData.endDate ? new Date(`${syncData.endDate}T23:59:59`) : null;
            const inRange = allResults.filter(item => {
                if (!item.data_emissao) return true;
                const emissionDate = new Date(item.data_emissao);
                if (startBoundary && emissionDate < startBoundary) return false;
                if (endBoundary && emissionDate > endBoundary) return false;
                return true;
            });

            let savedCount = 0;
            for (const item of inRange) {
                const { serie, numero } = parseChaveNFe(item.chave_nfe);
                const record = {
                    chaveAcesso: item.chave_nfe,
                    numero,
                    serie,
                    dataEmissao: item.data_emissao,
                    valorTotal: parseFloat(item.valor_total) || 0,
                    emitente: {
                        nome: item.nome_emitente,
                        cnpj: item.documento_emitente
                    },
                    fornecedorNome: item.nome_emitente,
                    fornecedorCnpj: item.documento_emitente,
                    manifestStatus: mapManifestacaoToStatus(item.manifestacao_destinatario),
                    sefazSituacao: item.situacao,
                    focusNfeVersao: item.versao,
                    digestValue: item.digest_value,
                    updatedAt: new Date()
                };

                const existing = nfes.find(n => n.chaveAcesso === item.chave_nfe);
                if (existing) {
                    await updateDocument(existing.id, record);
                } else {
                    await addDocument({ ...record, createdAt: new Date() });
                }
                savedCount += 1;
            }

            await refreshData();
            addToast(t('pages.recipientManifest.toasts.syncSuccess', { count: savedCount }), 'success');
            setShowSyncModal(false);
        } catch (error) {
            console.error('Sync error:', error);
            addToast(error.message || t('pages.recipientManifest.toasts.syncError'), 'error');
        } finally {
            setSyncInProgress(false);
        }
    };

    const statusFilterOptions = [
        { value: '', label: t('pages.recipientManifest.status.all') },
        { value: MANIFEST_STATUS.PENDING, label: t('pages.recipientManifest.status.pending') },
        { value: MANIFEST_STATUS.CONFIRMED, label: t('pages.recipientManifest.status.confirmed') },
        { value: MANIFEST_STATUS.DENIED, label: t('pages.recipientManifest.status.denied') },
        { value: MANIFEST_STATUS.UNKNOWN, label: t('pages.recipientManifest.status.unknown') }
    ];

    const isLoadingDisplay = isInitialLoad || isLoading;

    return (
        <div className={styles.pageContainer}>
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {selectedNFe && (
                <NFeDetailModal
                    nfe={selectedNFe}
                    onClose={() => setSelectedNFe(null)}
                    onPrint={() => handlePrint(selectedNFe)}
                    onManifest={(nfe) => { setSelectedNFe(null); setManifestingNFe(nfe); }}
                    isPrinting={isPrinting}
                    t={t}
                />
            )}

            {manifestingNFe && (
                <ManifestModal
                    nfe={manifestingNFe}
                    onClose={() => setManifestingNFe(null)}
                    onConfirm={handleManifestConfirm}
                    isLoading={isLoading}
                    t={t}
                />
            )}

            {showSyncModal && (
                <SyncNFeModal
                    onClose={() => setShowSyncModal(false)}
                    onSync={handleSync}
                    isLoading={syncInProgress}
                    cnpj={companyCnpj}
                    t={t}
                />
            )}

            <PageHeader
                title={t('pages.recipientManifest.title')}
                subtitle={t('pages.recipientManifest.subtitle')}
                actionText={can.sync ? `🔄 ${t('pages.recipientManifest.syncNFe')}` : null}
                onAction={() => setShowSyncModal(true)}
                showAction={can.sync}
                actionDisabled={syncInProgress}
            />

            {loadError && (
                <div className={styles.infoBox} style={{ backgroundColor: '#f8d7da', color: '#721c24', marginBottom: '20px' }}>
                    <span>⚠️ {t('pages.recipientManifest.errorLoading', { error: loadError })}</span>
                </div>
            )}

            <div className={styles.statusFilterBar}>
                {statusFilterOptions.map(opt => (
                    <button
                        key={opt.value || 'all'}
                        onClick={() => { setStatusFilter(opt.value); setCurrentPage(1); }}
                        className={`${styles.filterChip} ${statusFilter === opt.value ? styles.filterChipActive : ''}`}
                    >
                        {opt.label}
                        {opt.value && (
                            <span style={{ marginLeft: '8px', fontSize: '0.7rem', opacity: 0.8 }}>
                                ({nfes.filter(n => n.manifestStatus === opt.value).length})
                            </span>
                        )}
                    </button>
                ))}
            </div>

            <div className={styles.searchBar}>
                <SearchBar
                    placeholder={t('pages.recipientManifest.searchPlaceholder')}
                    onSearch={(term) => {
                        setSearchTerm(term);
                        setCurrentPage(1);
                    }}
                    initialValue={searchTerm}
                />
            </div>

            {isLoadingDisplay && nfes.length === 0 && !loadError ? (
                <TableSkeleton columns={7} rows={ITEMS_PER_PAGE} />
            ) : currentNfes && currentNfes.length > 0 ? (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>{t('pages.recipientManifest.table.number')}</th>
                                <th>{t('pages.recipientManifest.table.series')}</th>
                                <th>{t('pages.recipientManifest.table.issuer')}</th>
                                <th>{t('pages.recipientManifest.table.emissionDate')}</th>
                                <th>{t('pages.recipientManifest.table.totalValue')}</th>
                                <th>{t('pages.recipientManifest.table.manifestStatus')}</th>
                                <th style={{ width: '200px' }}>{t('pages.recipientManifest.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentNfes.map(nfe => (
                                <tr key={nfe.id} className={styles.tableRow}>
                                    <td><strong>{nfe.numero}</strong></td>
                                    <td>{nfe.serie || '-'}</td>
                                    <td>{nfe.emitente?.nome || nfe.fornecedorNome || '-'}</td>
                                    <td>{nfe.dataEmissao}</td>
                                    <td className={styles.priceCell}>
                                        R$ {(nfe.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td>
                                        <StatusBadge status={nfe.manifestStatus} />
                                    </td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            {can.view && (
                                                <button onClick={() => setSelectedNFe(nfe)} className={`${styles.actionButton} ${styles.viewButton}`} title={t('common.view')}>
                                                    👁️
                                                </button>
                                            )}
                                            {can.print && (
                                                <button onClick={() => handlePrint(nfe)} className={`${styles.actionButton} ${styles.paymentButton}`} disabled={isPrinting} title={t('common.print')}>
                                                    🖨️
                                                </button>
                                            )}
                                            {can.manifest && nfe.manifestStatus === MANIFEST_STATUS.PENDING && (
                                                <button onClick={() => setManifestingNFe(nfe)} className={`${styles.actionButton} ${styles.confirmActionButton}`} title={t('common.manifest')} style={{ color: 'green' }}>
                                                    📝
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <EmptyState
                    title={loadError ? t('pages.recipientManifest.errorLoading', { error: loadError }) :
                        (searchTerm || statusFilter) ? t('pages.recipientManifest.noNFeWithFilters') :
                            companyCnpj ? t('pages.recipientManifest.noNFeFound') :
                                t('pages.recipientManifest.noNFeConfigureCnpj')}
                    actionText={can.sync ? `🔄 ${t('pages.recipientManifest.syncNFe')}` : null}
                    onAction={() => setShowSyncModal(true)}
                />
            )}

            {!isLoadingDisplay && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredNfes.length}
                    onPageChange={setCurrentPage}
                    itemsLabel={t('common.items')}
                />
            )}
        </div>
    );
};

export default ManifestoDestinatarioPage;